import { format } from "date-fns";
import clsx from "clsx";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { btnIconCls } from "components/tw/classes";
import { getMonthGridWeeks, weekdayInitials } from "containers/user-profile-leaves/leaveWallCalendar.helpers";
import { dayKey, leaveCalendarDateLocale } from "features/leave-calendar/leaveCalendarDates";
import { useLanguage } from "utils/use-language";
import type { PickedRange } from "./leave-range";

interface RangeCalendarProps {
  month: Date;
  picked: PickedRange | null;
  /** Days that cannot be tapped: past days, weekends and holidays. */
  isDayDisabled: (day: string) => boolean;
  canGoToPreviousMonth: boolean;
  onTapDay: (day: string) => void;
  onChangeMonth: (months: number) => void;
}

/** One month, Monday first. Days of the picked range are filled in; the first and last day are darker. */
export default function RangeCalendar({
  month,
  picked,
  isDayDisabled,
  canGoToPreviousMonth,
  onTapDay,
  onChangeMonth,
}: RangeCalendarProps) {
  const { t } = useTranslation();
  const locale = leaveCalendarDateLocale(useLanguage());
  const days = getMonthGridWeeks(month.getFullYear(), month.getMonth()).flat();

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          className={`${btnIconCls} max-md:size-11`}
          aria-label={t("leave_sheet_previous_month")}
          disabled={!canGoToPreviousMonth}
          onClick={() => onChangeMonth(-1)}
        >
          <ChevronLeft className="size-5" aria-hidden="true" />
        </button>
        <p className="text-sm font-semibold capitalize text-gray-900">{format(month, "LLLL yyyy", { locale })}</p>
        <button
          type="button"
          className={`${btnIconCls} max-md:size-11`}
          aria-label={t("leave_sheet_next_month")}
          onClick={() => onChangeMonth(1)}
        >
          <ChevronRight className="size-5" aria-hidden="true" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-y-1 text-center">
        {weekdayInitials(locale).map((initials, index) => (
          <span key={index} className="pb-1 text-xs text-gray-500">
            {initials}
          </span>
        ))}
        {days.map((date) => {
          // The grid is padded with the days of the neighbouring months; those stay blank.
          if (date.getMonth() !== month.getMonth()) return <span key={dayKey(date)} />;
          const day = dayKey(date);
          const isEdge = day === picked?.start || day === picked?.end;
          const isInRange = picked !== null && day >= picked.start && day <= picked.end;
          return (
            <button
              key={day}
              type="button"
              aria-label={format(date, "PPP", { locale })}
              aria-pressed={isEdge}
              disabled={isDayDisabled(day)}
              onClick={() => onTapDay(day)}
              className={clsx(
                "mx-auto size-11 rounded-lg text-sm md:size-9",
                isEdge && "bg-indigo-600 font-semibold text-white",
                isInRange && !isEdge && "bg-indigo-100 text-indigo-900",
                !isInRange && "text-gray-900 hover:bg-gray-100",
                "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 disabled:hover:bg-gray-100"
              )}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
